import api from "@/services/api";
import { normalizeTelemetryPagePath } from "@/utils/telemetry";
import { ensureTelemetrySession, getClientVersion } from "@/utils/telemetryContext";

export type PerformanceMetricName = "ttfb" | "dom_load" | "load" | "lcp" | "cls" | "inp";
type Rating = "good" | "needs_improvement" | "poor";

interface Metric {
  name: PerformanceMetricName;
  value: number;
  rating: Rating;
}

function rating(name: PerformanceMetricName, value: number): Rating {
  const thresholds: Record<PerformanceMetricName, [number, number]> = {
    ttfb: [800, 1_800],
    dom_load: [2_000, 4_000],
    load: [2_500, 5_000],
    lcp: [2_500, 4_000],
    cls: [0.1, 0.25],
    inp: [200, 500],
  };
  const [good, poor] = thresholds[name];
  return value <= good ? "good" : value <= poor ? "needs_improvement" : "poor";
}

function sendMetrics(userId: string, metrics: Metric[]): void {
  if (metrics.length === 0) return;
  const session = ensureTelemetrySession(userId);
  void api.post("/api/observability/performance", {
    sessionId: session.sessionId,
    pagePath: normalizeTelemetryPagePath(`${window.location.pathname}${window.location.search}`),
    appVersion: getClientVersion(),
    metrics,
  }, { keepalive: true }).catch(() => {});
}

export function startPerformanceTracking(userId: string): () => void {
  const observers: PerformanceObserver[] = [];
  const sent = new Set<PerformanceMetricName>();
  const pending: Metric[] = [];
  let sendTimer: ReturnType<typeof setTimeout> | null = null;
  let lcp = 0;
  let cls = 0;
  let inp = 0;

  const flushPending = () => {
    if (sendTimer) clearTimeout(sendTimer);
    sendTimer = null;
    if (pending.length === 0) return;
    sendMetrics(userId, pending.splice(0));
  };

  const sendOnce = (name: PerformanceMetricName, value: number) => {
    if (sent.has(name) || !Number.isFinite(value) || value < 0) return;
    sent.add(name);
    const rounded = name === "cls" ? Math.round(value * 10_000) / 10_000 : Math.round(value * 100) / 100;
    pending.push({ name, value: rounded, rating: rating(name, rounded) });
    if (!sendTimer) sendTimer = setTimeout(flushPending, 1_000);
  };

  const captureNavigation = () => {
    const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    if (!navigation) return;
    sendOnce("ttfb", navigation.responseStart - navigation.requestStart);
    sendOnce("dom_load", navigation.domContentLoadedEventEnd - navigation.startTime);
    sendOnce("load", navigation.loadEventEnd - navigation.startTime);
  };
  if (document.readyState === "complete") captureNavigation();
  else window.addEventListener("load", captureNavigation, { once: true });

  const observe = (type: string, callback: PerformanceObserverCallback) => {
    if (typeof PerformanceObserver === "undefined"
        || !PerformanceObserver.supportedEntryTypes.includes(type)) return;
    try {
      const observer = new PerformanceObserver(callback);
      observer.observe({ type, buffered: true } as PerformanceObserverInit);
      observers.push(observer);
    } catch { /* unsupported observer option */ }
  };

  observe("largest-contentful-paint", (list) => {
    const entries = list.getEntries();
    const last = entries[entries.length - 1];
    if (last) lcp = last.startTime;
  });
  observe("layout-shift", (list) => {
    for (const entry of list.getEntries()) {
      const shift = entry as PerformanceEntry & { value?: number; hadRecentInput?: boolean };
      if (!shift.hadRecentInput) cls += shift.value ?? 0;
    }
  });
  observe("event", (list) => {
    for (const entry of list.getEntries()) inp = Math.max(inp, entry.duration);
  });

  const flushVitals = () => {
    if (lcp > 0) sendOnce("lcp", lcp);
    sendOnce("cls", cls);
    if (inp > 0) sendOnce("inp", inp);
  };
  const flushAll = () => {
    flushVitals();
    flushPending();
  };
  const visibilityHandler = () => {
    if (document.visibilityState === "hidden") flushAll();
  };
  document.addEventListener("visibilitychange", visibilityHandler);
  window.addEventListener("pagehide", flushAll);

  return () => {
    observers.forEach((observer) => observer.disconnect());
    document.removeEventListener("visibilitychange", visibilityHandler);
    window.removeEventListener("pagehide", flushAll);
    window.removeEventListener("load", captureNavigation);
    flushAll();
  };
}
